import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import logoIcon from "assets/images/logos/logo-icon.png";
import Loading from "components/Loading";
import AccountSetupFlow, { ACCOUNT_SETUP_STEPS } from "./AccountSetupFlow";
import MfaEnrollSteps from "./MfaEnrollSteps";
import ReauthDialog from "./ReauthDialog";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import {
  hasFacilityCardClaim,
  needsMfaEnrollment as computeNeedsMfaEnrollment,
} from "utils/needsMfaEnrollment";

/**
 * Onboarding page - For users who have Firebase Auth but need to complete profile setup
 *
 * Uses the shared AccountSetupFlow component for the actual form steps.
 * Supports ?role=patient|provider|facility param to pre-select account type.
 */
const Onboarding = () => {
  const { user, userData, userLoading, logout, tokenClaims } = useAuth();
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const [searchParams] = useSearchParams();
  const pendingInviteToken = sessionStorage.getItem(
    "pendingOrganizationInviteToken",
  );
  // Role the invitation is for (stored by JoinPage). AccountSetupFlow speaks
  // "provider" rather than "professional"; patient/admin pass through unchanged.
  const pendingInviteRole = sessionStorage.getItem(
    "pendingOrganizationInviteRole",
  );
  const invitedRoleForSetup =
    pendingInviteRole === "professional" ? "provider" : pendingInviteRole;
  // pendingSignupRole is stashed by the public /share landing page so a provider's
  // intended role survives the email-verification round-trip that strips ?role=.
  // localStorage (not sessionStorage): the email link opens a fresh tab.
  const shareSignupRole = localStorage.getItem("pendingSignupRole");
  const preselectedRole =
    searchParams.get("role") ||
    (pendingInviteToken ? invitedRoleForSetup || "provider" : null) ||
    shareSignupRole;
  // Providers arriving from /share already declared their role, so skip the
  // account-type chooser entirely (auto-create the profile) just like invites do.
  const fromShareSignup = Boolean(shareSignupRole);
  // Product origin (e.g. "voxpont") stashed by AuthPage when the user arrived
  // via a branded entry point. Survives the email-verify round-trip in
  // localStorage; stamped onto the profile by AccountSetupFlow, then cleared
  // below once the role is committed.
  const pendingProductOrigin = localStorage.getItem("pendingProductOrigin");
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [step, setStep] = useState(ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE);
  const [isOnSuccessStep, setIsOnSuccessStep] = useState(false);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probedUserData, setProbedUserData] = useState(undefined);
  const [emailVerifiedRefreshLoading, setEmailVerifiedRefreshLoading] =
    useState(false);
  const [emailVerifiedOverride, setEmailVerifiedOverride] = useState(null);
  /** Blocks auto-redirect until AccountSetupFlow finishes commit (state so effects re-run when cleared). */
  const [signupInProgress, setSignupInProgress] = useState(false);
  const pendingReauthActionRef = useRef(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const navigate = useNavigate();
  const backHandlerRef = useRef(null);
  const effectiveUserData = userData === undefined ? probedUserData : userData;
  const isFacilityCardUser = hasFacilityCardClaim(tokenClaims);
  const effectiveEmailVerified =
    isFacilityCardUser ||
    isDevAuthFirestoreUser(effectiveUserData) ||
    (emailVerifiedOverride === null
      ? !!user?.emailVerified
      : emailVerifiedOverride);
  const needsAccountSetup =
    !effectiveUserData?.role || effectiveUserData?.onboarding === true;
  const needsMfaEnrollment = computeNeedsMfaEnrollment({
    user,
    isEnrolled,
    skipUi: shouldSkipMfaEnrollmentUi(),
    claims: tokenClaims,
  });
  // VoxPont users never use the FusionMSI account-type onboarding — they pick a
  // VoxPont product instead. Detect them via the stashed product origin or their
  // profile and route them to the VoxPont picker before AccountSetupFlow renders.
  const isVoxPontUser =
    pendingProductOrigin === "voxpont" ||
    effectiveUserData?.product === "voxpont";

  // Resolve the "undefined userData" ambiguity: still loading vs missing doc.
  useEffect(() => {
    let cancelled = false;
    const refreshEmailVerified = async () => {
      if (!user) {
        setEmailVerifiedOverride(null);
        return;
      }
      if (user.emailVerified) {
        setEmailVerifiedOverride(true);
        return;
      }
      setEmailVerifiedRefreshLoading(true);
      try {
        await firebase.auth().currentUser?.reload();
        if (cancelled) return;
        setEmailVerifiedOverride(!!firebase.auth().currentUser?.emailVerified);
      } catch (err) {
        if (cancelled) return;
        setEmailVerifiedOverride(!!user.emailVerified);
      } finally {
        if (!cancelled) setEmailVerifiedRefreshLoading(false);
      }
    };
    refreshEmailVerified();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Redirect VoxPont users out of HealthDesk onboarding to the VoxPont picker.
  useEffect(() => {
    if (isVoxPontUser) {
      navigate("/voxpont-start", { replace: true });
    }
  }, [isVoxPontUser, navigate]);

  // Once a role is committed, drop the share-flow role hint so a later signup on
  // this device isn't pre-selected as a provider.
  useEffect(() => {
    if (effectiveUserData?.role) {
      localStorage.removeItem("pendingSignupRole");
      localStorage.removeItem("pendingProductOrigin");
    }
  }, [effectiveUserData?.role]);

  useEffect(() => {
    let cancelled = false;
    const probeUserDoc = async () => {
      if (!user || userData !== undefined) {
        setProbeLoading(false);
        setProbedUserData(undefined);
        return;
      }
      setProbeLoading(true);
      try {
        const snap = await db.collection("users").doc(user.uid).get();
        if (cancelled) return;
        setProbedUserData(snap.exists ? snap.data() : null);
      } catch (err) {
        if (cancelled) return;
        // Fail-open: avoid deadlock by allowing onboarding to continue.
        setProbedUserData(null);
      } finally {
        if (!cancelled) setProbeLoading(false);
      }
    };
    probeUserDoc();
    return () => {
      cancelled = true;
    };
  }, [user, userData]);

  // Check auth state and redirect if needed
  useEffect(() => {
    if (
      userLoading ||
      sessionMfaLoading ||
      probeLoading ||
      emailVerifiedRefreshLoading
    )
      return;

    if (user) {
      // Invitees haven't proved email ownership yet at the Auth layer — clicking
      // the invite link is the proof. acceptInvitation marks emailVerified=true
      // server-side once the user doc lands. Skip the bounce so the invitation
      // flow can complete instead of getting kicked back to /auth.
      if (!effectiveEmailVerified && !pendingInviteToken) {
        navigate("/auth");
        return;
      }
      if (
        !needsAccountSetup &&
        !needsMfaEnrollment &&
        !signupInProgress
      ) {
        navigate("/dashboard");
        return;
      }
      setLoading(false);
    } else {
      // No authenticated user - redirect to auth
      navigate("/auth");
    }
  }, [
    navigate,
    needsAccountSetup,
    needsMfaEnrollment,
    signupInProgress,
    sessionMfaLoading,
    probeLoading,
    emailVerifiedRefreshLoading,
    effectiveEmailVerified,
    user,
    userData,
    userLoading,
  ]);

  const handleComplete = async () => {
    if (!pendingInviteToken) return;
    const acceptInvitation = firebase
      .functions()
      .httpsCallable("acceptInvitation");
    try {
      await acceptInvitation({ token: pendingInviteToken });
      sessionStorage.removeItem("pendingOrganizationInviteToken");
      sessionStorage.removeItem("pendingOrganizationInviteRole");
      // acceptInvitation flips emailVerified=true server-side. Reload the
      // local Auth user so DashboardPage's verification gate sees the change.
      try {
        await firebase.auth().currentUser?.reload();
        setEmailVerifiedOverride(
          !!firebase.auth().currentUser?.emailVerified,
        );
      } catch {
        /* ignore — DashboardPage has a fallback "resend verification" screen */
      }
    } catch (err) {
      const code = err?.code || "";
      const message = (err?.message || "").toLowerCase();
      const isTerminal =
        code.includes("already-exists") ||
        code.includes("permission-denied") ||
        code.includes("not-found") ||
        (code.includes("failed-precondition") &&
          (message.includes("accepted") ||
            message.includes("revoked") ||
            message.includes("expired") ||
            message.includes("different email") ||
            message.includes("organization")));
      if (isTerminal) {
        sessionStorage.removeItem("pendingOrganizationInviteToken");
        sessionStorage.removeItem("pendingOrganizationInviteRole");
        return;
      }
      throw err;
    }
  };

  const handleBack = () => {
    backHandlerRef.current?.();
  };

  if (loading || probeLoading || emailVerifiedRefreshLoading)
    return <Loading page />;

  // VoxPont users are being redirected to /voxpont-start by the effect above —
  // hold a loading screen rather than flashing the account-type chooser.
  if (isVoxPontUser) return <Loading page />;

  // Account setup done and MFA not required — brief state before navigate to dashboard
  if (!needsAccountSetup && !needsMfaEnrollment) {
    return <Loading page />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      {/* Logo */}
      <Box
        component="img"
        src={logoIcon}
        alt="FusionMSI"
        sx={{ height: 48, mb: 4 }}
      />

      {/* Main content container */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          px: 3,
        }}
      >
        {/* Back button - only during account setup flow */}
        {needsAccountSetup &&
          step !== ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE &&
          !isOnSuccessStep && (
            <Box sx={{ mb: 2 }}>
              <IconButton onClick={handleBack} edge="start" sx={{ ml: -1 }}>
                <ArrowBack />
              </IconButton>
            </Box>
          )}

        {needsAccountSetup ? (
          <AccountSetupFlow
            onComplete={handleComplete}
            onStepChange={setStep}
            backHandlerRef={backHandlerRef}
            preselectedRole={preselectedRole}
            autoCreateProfile={Boolean(pendingInviteToken) || fromShareSignup}
            isInvitationFlow={Boolean(pendingInviteToken)}
            inviteToken={pendingInviteToken}
            productOrigin={pendingProductOrigin}
            onSignupStart={() => setSignupInProgress(true)}
            onSignupEnd={() => setSignupInProgress(false)}
            onSuccessStep={setIsOnSuccessStep}
          />
        ) : (
          <MfaEnrollSteps
            onDone={() => navigate("/dashboard")}
            onRequireReauth={(retryFn) => {
              pendingReauthActionRef.current = retryFn;
              setReauthOpen(true);
            }}
          />
        )}

        {/* Footer with logout - hide on success step */}
        {!isOnSuccessStep && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 4, textAlign: "center", display: "block" }}
          >
            <MuiLink
              component="button"
              variant="caption"
              color="text.disabled"
              underline="hover"
              onClick={async () => {
                setLogoutLoading(true);
                try {
                  await logout();
                  navigate("/");
                } catch (err) {
                  console.error("Logout error:", err);
                  setLogoutLoading(false);
                }
              }}
              disabled={logoutLoading}
              sx={{ verticalAlign: "baseline" }}
            >
              {logoutLoading ? "Logging out..." : "Log out"}
            </MuiLink>
          </Typography>
        )}
      </Box>
      <ReauthDialog
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onSuccess={async () => {
          const action = pendingReauthActionRef.current;
          pendingReauthActionRef.current = null;
          setReauthOpen(false);
          if (action) await action();
        }}
      />
    </Box>
  );
};

export default Onboarding;
